import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  Brain,
  Building2,
  Compass,
  Eye,
  Layers,
  Link2,
  Moon,
  Trophy,
  Users,
  Waves,
} from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import NextStep from "@/components/layout/NextStep";
import { Reveal, SectionOverline } from "@/components/landing/Shared";
import AdaptiveLoopHeader from "@/components/adaptive-loop/AdaptiveLoopHeader";

const PRACTICE_STEPS = [
  {
    number: "01",
    icon: Eye,
    title: "Assess the current state",
    body:
      "Begin with the person’s present physiological, emotional, behavioural, and environmental response rather than assuming the same intervention fits every moment.",
  },
  {
    number: "02",
    icon: Activity,
    title: "Match the physiological input",
    body:
      "Select movement, exertion, breathing, mobility, recovery, environmental change, or another practical input that fits the current state, readiness, and capacity.",
  },
  {
    number: "03",
    icon: Waves,
    title: "Complete the recovery transition",
    body:
      "The output is not complete when the activity stops. Recovery, pacing, cooldown, and parasympathetic access are treated as part of the intervention itself.",
  },
  {
    number: "04",
    icon: Link2,
    title: "Observe, learn, and adapt",
    body:
      "Record what changed in tension, energy, attention, urgency, emotion, and behaviour, then use that response to improve the next match.",
  },
];

const KNOWLEDGE_PILLARS = [
  {
    number: "01",
    icon: Activity,
    title: "Exercise Physiology",
    lead:
      "How movement dose, modality, timing, intensity, and recovery change the physiological state.",
    practice: [
      "Strength and conditioning, exercise therapy, corrective exercise, cardiopulmonary conditioning, and activity progression.",
    ],
  },
  {
    number: "02",
    icon: Brain,
    title: "Neuroplasticity",
    lead:
      "How repeated experience and completed adaptive cycles shape the nervous system’s available responses.",
    practice: [
      "Addiction-recovery programming, habit retraining, behavioural substitution, skill development, and long-term coaching.",
    ],
  },
  {
    number: "03",
    icon: Waves,
    title: "Stress Physiology",
    lead:
      "How load, autonomic activation, recovery capacity, and repeated demand influence regulation.",
    practice: [
      "Autonomic-regulation work, addiction-recovery settings, chronic stress, burnout, and recovery coaching.",
    ],
  },
  {
    number: "04",
    icon: Users,
    title: "Behavioral Science",
    lead:
      "How environment, reinforcement, repetition, social context, and physiological state shape behaviour.",
    practice: [
      "Adherence coaching, behavioural coaching, environmental design, reinforcement, recovery coaching, and life coaching.",
    ],
  },
  {
    number: "05",
    icon: Moon,
    title: "Recovery Science",
    lead:
      "How restoration, pacing, sleep, rhythm, and parasympathetic access support adaptation.",
    practice: [
      "Recovery programming, chronic-pain rehabilitation, mobility, daily rhythm, recreation, cooldown, and sleep-support practice.",
    ],
  },
  {
    number: "06",
    icon: Trophy,
    title: "Human Performance",
    lead:
      "How autonomic capacity supports repeatable physical and behavioural output under demand.",
    practice: [
      "Strength and performance coaching, combat sports, team athletes, individual athletes, and long-term athlete development.",
    ],
  },
];

const APPLICATION_CONTEXTS = [
  {
    icon: Compass,
    title: "Recovery and health support",
    body:
      "Use state-matched activity, pacing, recovery, and environmental inputs alongside appropriate clinical or professional care.",
  },
  {
    icon: Link2,
    title: "Behaviour and dependency patterns",
    body:
      "Interrupt the space between activation and automatic relief by introducing a practical adaptive response the person can repeat.",
  },
  {
    icon: Trophy,
    title: "Training and performance",
    body:
      "Match physical demand to readiness, conditioning, recovery capacity, and the specific performance state being developed.",
  },
  {
    icon: Building2,
    title: "Professional integration",
    body:
      "Give clinicians, counsellors, coaches, trainers, and rehabilitation professionals a shared physiological implementation layer.",
  },
];

const DEVELOPMENT_AREAS = [
  {
    title: "Assessment",
    body:
      "Translate self-reported state, inputs, capacity, behaviour, and environment into an Adaptive Profile and practical plan.",
  },
  {
    title: "Adaptive Companion",
    body:
      "Support ongoing check-ins, state-matched interventions, alternatives, response tracking, and mobile access.",
  },
  {
    title: "Professional application",
    body:
      "Develop consultation, education, implementation resources, and future training for clinical and coaching settings.",
  },
];

const Origin = () => {
  return (
    <PageShell>
      <section
        data-testid="origin-hero"
        className="relative pt-28 sm:pt-36 pb-16 sm:pb-20 border-b border-line-soft forr-canvas overflow-hidden"
      >
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] forr-radial-teal opacity-60 pointer-events-none" />
        <div className="absolute top-40 right-[-200px] w-[500px] h-[500px] forr-radial-warm opacity-30 pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <nav
              aria-label="Breadcrumb"
              data-testid="origin-breadcrumb"
              className="flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] forr-text-mute mb-10"
            >
              <Link to="/" className="hover:forr-text-teal-deep transition-colors">
                Home
              </Link>
              <span className="forr-text-faint">›</span>
              <span className="forr-text-soft">About</span>
            </nav>
          </Reveal>

          <div className="grid grid-cols-12 gap-8 items-center">
            <div className="col-span-12 lg:col-span-8">
              <Reveal>
                <div className="inline-flex items-center gap-3 mb-10">
                  <span className="forr-hairline" />
                  <span className="forr-overline">About Adaptive Physiology</span>
                </div>
              </Reveal>
              <Reveal delay={0.05}>
                <h1
                  data-testid="origin-headline"
                  className="font-display forr-text-ink text-[40px] sm:text-6xl md:text-7xl lg:text-[82px] leading-[1.02] font-medium tracking-[-0.03em]"
                >
                  Assess the state. Match the input.{" "}
                  <span className="font-editorial forr-text-teal-deep">
                    Adapt the response.
                  </span>
                </h1>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-10 max-w-3xl text-lg sm:text-xl font-light forr-text-soft leading-relaxed">
                  Adaptive Physiology is a practice-led methodology for using
                  physiological inputs, recovery, environment, and repeated
                  observation to support different emotional and behavioural
                  responses.
                </p>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-4 flex justify-center lg:justify-end">
              <Reveal delay={0.1} className="w-full max-w-[350px]">
                <AdaptiveLoopHeader testId="origin-header-loop" />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="origin-observation"
        className="relative py-24 sm:py-32 forr-canvas-alt"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <SectionOverline
                  number="01"
                  label="The Methodology"
                  testId="origin-observation-overline"
                />
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="mt-8 font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
                  Behaviour is not separated from the state producing it.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 lg:col-span-8 lg:pl-8">
              <div className="space-y-6 max-w-3xl">
                <Reveal delay={0.1}>
                  <p className="text-xl sm:text-2xl forr-text-ink leading-relaxed">
                    The nervous system continuously organizes energy, attention,
                    muscular readiness, urgency, recovery, and the range of
                    responses available in the moment.
                  </p>
                </Reveal>
                <Reveal delay={0.15}>
                  <p className="text-lg font-light forr-text-soft leading-relaxed">
                    Adaptive Physiology begins by identifying that current state.
                    It then selects a practical physiological input that the
                    person can receive, complete, and observe. The goal is not to
                    force one ideal state. The goal is to improve the system’s
                    ability to move between activation, action, recovery, and
                    renewed capacity.
                  </p>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="text-lg font-light forr-text-soft leading-relaxed">
                    Movement can increase or organize activation. Recovery can
                    reduce unnecessary carryover. Environment can change the
                    signal being received. Repetition can make a different
                    response easier to access the next time demand appears.
                  </p>
                </Reveal>
              </div>

              <div className="mt-12 grid sm:grid-cols-3 gap-4">
                {[
                  {
                    title: "State before strategy",
                    body:
                      "Interpret what the system is doing before deciding what it should do next.",
                  },
                  {
                    title: "Input before instruction",
                    body:
                      "Use an appropriate physiological experience rather than relying on explanation alone.",
                  },
                  {
                    title: "Recovery completes the cycle",
                    body:
                      "Treat the transition out of demand as part of the intervention, not an optional extra.",
                  },
                ].map((item) => (
                  <Reveal key={item.title}>
                    <div className="forr-card rounded-[20px] p-6 h-full">
                      <h3 className="font-display forr-text-ink text-lg font-medium tracking-tight">
                        {item.title}
                      </h3>
                      <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                        {item.body}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        data-testid="origin-practice"
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="02"
              label="The Practice"
              testId="origin-practice-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-4xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              Four steps guide each application.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg font-light forr-text-soft leading-relaxed">
              The specific intervention can change with the person, setting,
              capacity, and current state. The decision sequence remains stable.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-4 gap-5">
            {PRACTICE_STEPS.map((step, index) => (
              <Reveal delay={index * 0.05} key={step.number}>
                <article className="forr-card rounded-[24px] p-7 h-full">
                  <div className="flex items-center justify-between gap-4">
                    <div
                      className="h-11 w-11 rounded-2xl flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--forr-teal-tint)",
                        color: "var(--forr-teal-deep)",
                      }}
                    >
                      <step.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.24em] forr-text-faint">
                      Step {step.number}
                    </span>
                  </div>
                  <h3 className="mt-7 font-display forr-text-ink text-xl font-medium tracking-tight">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                    {step.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="origin-integration"
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="03"
              label="Knowledge Pillars in Practice"
              testId="origin-integration-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-4xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              Six knowledge pillars support the methodology.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg font-light forr-text-soft leading-relaxed">
              Adaptive Physiology integrates established scientific disciplines
              with applied practice across training, rehabilitation, recovery,
              behaviour change, and human performance.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-3 gap-6">
            {KNOWLEDGE_PILLARS.map((pillar, index) => (
              <Reveal delay={index * 0.05} key={pillar.title}>
                <article
                  data-testid={`origin-pillar-${pillar.number}`}
                  className="forr-card rounded-[24px] p-7 sm:p-8 h-full"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div
                      className="h-11 w-11 rounded-2xl flex items-center justify-center"
                      style={{
                        backgroundColor: "var(--forr-teal-tint)",
                        color: "var(--forr-teal-deep)",
                      }}
                    >
                      <pillar.icon size={20} strokeWidth={1.5} />
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.24em] forr-text-faint">
                      Pillar {pillar.number}
                    </span>
                  </div>
                  <h3 className="mt-7 font-display forr-text-ink text-2xl font-medium tracking-tight">
                    {pillar.title}
                  </h3>
                  <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                    {pillar.lead}
                  </p>
                  <div
                    className="mt-7 rounded-2xl p-5"
                    style={{
                      backgroundColor: "var(--forr-teal-tint)",
                      border: "1px solid var(--forr-teal-soft)",
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.24em] forr-text-teal-deep">
                      Relevant applied practice
                    </div>
                    <ul className="mt-3 space-y-3">
                      {pillar.practice.map((item) => (
                        <li
                          key={item}
                          className="text-sm font-light forr-text-ink leading-relaxed"
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        id="forr-health"
        data-testid="origin-forr"
        className="relative py-24 sm:py-32 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="04"
              label="Applied Methodology"
              testId="origin-forr-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-4xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              Different settings. One operating principle.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg font-light forr-text-soft leading-relaxed">
              Change the physiological conditions shaping the response, then
              observe whether the person gains a more useful range of action,
              recovery, and behaviour.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-2 xl:grid-cols-4 gap-5">
            {APPLICATION_CONTEXTS.map((context, index) => (
              <Reveal delay={index * 0.05} key={context.title}>
                <article className="forr-card rounded-[22px] p-7 h-full">
                  <div
                    className="h-11 w-11 rounded-2xl flex items-center justify-center"
                    style={{
                      backgroundColor: "var(--forr-teal-tint)",
                      color: "var(--forr-teal-deep)",
                    }}
                  >
                    <context.icon size={20} strokeWidth={1.5} />
                  </div>
                  <h3 className="mt-6 font-display forr-text-ink text-xl font-medium tracking-tight">
                    {context.title}
                  </h3>
                  <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                    {context.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.25}>
            <div
              data-testid="origin-founder-attribution"
              className="mt-12 flex items-start gap-4 p-6 rounded-2xl forr-canvas-warm max-w-4xl"
              style={{ border: "1px solid var(--forr-line-soft)" }}
            >
              <div
                className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center"
                style={{
                  backgroundColor: "var(--forr-teal-soft)",
                  color: "var(--forr-teal-deep)",
                }}
              >
                <Compass size={16} strokeWidth={1.6} />
              </div>
              <div className="flex-1">
                <div className="text-[11px] uppercase tracking-[0.28em] forr-text-mute">
                  Founder consultation
                </div>
                <p className="mt-2 forr-text-ink font-light leading-relaxed">
                  Questions about the practice or its use in a clinical setting
                  can be directed through a{" "}
                  <Link
                    to="/programs/professional-consultation"
                    className="forr-text-teal-deep hover:underline underline-offset-4"
                  >
                    professional consultation
                  </Link>{" "}
                  with its founder, Corey Forrester.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section
        data-testid="origin-future"
        className="relative py-24 sm:py-32 forr-canvas-alt border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="05"
              label="Development and Stewardship"
              testId="origin-future-overline"
            />
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-8 max-w-4xl font-display forr-text-ink text-3xl sm:text-4xl md:text-5xl font-medium tracking-[-0.02em] leading-[1.08]">
              FORR Health develops the methodology and the tools that apply it.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg font-light forr-text-soft leading-relaxed">
              The work is organized around making Adaptive Physiology clearer,
              more testable, more practical, and easier to integrate into
              appropriate individual and professional settings.
            </p>
          </Reveal>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            {DEVELOPMENT_AREAS.map((area, index) => (
              <Reveal delay={index * 0.06} key={area.title}>
                <article className="forr-card rounded-[22px] p-7 h-full">
                  <div className="text-[10px] uppercase tracking-[0.24em] forr-text-teal-deep">
                    In development
                  </div>
                  <h3 className="mt-4 font-display forr-text-ink text-xl font-medium tracking-tight">
                    {area.title}
                  </h3>
                  <p className="mt-3 text-sm font-light forr-text-soft leading-relaxed">
                    {area.body}
                  </p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section
        data-testid="origin-related"
        className="relative py-20 sm:py-24 forr-canvas border-t border-line-soft"
      >
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal>
            <SectionOverline
              number="→"
              label="Related Reading"
              testId="origin-related-overline"
            />
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                to: "/methodology",
                label: "The Methodology",
                desc: "Adaptive Physiology at a glance.",
              },
              {
                to: "/methodology/framework",
                label: "The Framework",
                desc: "How the methodology organizes state, input, adaptation, and recovery.",
              },
              {
                to: "/methodology/science",
                label: "Scientific Foundations",
                desc: "The evidence and physiological concepts supporting the work.",
              },
              {
                to: "/programs/professional-consultation",
                label: "Professional Consultation",
                desc: "Discuss appropriate clinical, coaching, or organizational application.",
              },
            ].map((item) => (
              <Reveal key={item.to}>
                <Link
                  to={item.to}
                  data-testid={`origin-related-${item.to.split("/").pop()}`}
                  className="forr-card rounded-[20px] p-6 block h-full group"
                >
                  <div className="text-[11px] uppercase tracking-[0.24em] forr-text-mute mb-3">
                    Explore
                  </div>
                  <div className="font-display forr-text-ink text-lg font-medium tracking-tight">
                    {item.label}
                  </div>
                  <div className="mt-2 text-sm forr-text-mute font-light leading-relaxed">
                    {item.desc}
                  </div>
                  <div className="mt-6 inline-flex items-center gap-2 forr-text-teal-deep text-sm">
                    Open
                    <ArrowRight
                      size={14}
                      strokeWidth={1.75}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <NextStep
        overline="Continue"
        title="Explore the methodology"
        description="Review the framework, science, applications, and Adaptive Loop that organize the practice."
        to="/methodology"
        cta="Open methodology"
        testId="origin-next"
      />
    </PageShell>
  );
};

export default Origin;
